import { Box, Chip, MenuItem, TextField, Typography } from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import { Check, Plus, RefreshCw, Tag, Trash2 } from 'lucide-react';

import { Stack } from '$layouts/v2/components/Stack.jsx';
import type { FieldInfo } from '$layouts/v2/helpers/context.js';

import {
  ReadOnlyTextField,
  StepSection,
  TemplateField,
  Transitions,
  TransitionTargetSelect,
} from './StepFormPrimitives';

type StepOption = { id: string; label: string; kind?: v4.StepKind };

const OPERATION_OPTIONS: {
  value: v4.OutlookCategoryOperation;
  label: string;
  description: string;
  icon: typeof RefreshCw;
}[] = [
  {
    value: 'replaceManaged',
    label: 'Replace the workflow category',
    description: 'Remove an earlier ✨ or ✅ category, then set this one.',
    icon: RefreshCw,
  },
  {
    value: 'add',
    label: 'Add a category',
    description: 'Keep existing categories and add this one as well.',
    icon: Plus,
  },
  {
    value: 'remove',
    label: 'Remove a category',
    description:
      'Remove this workflow category and leave other categories alone.',
    icon: Trash2,
  },
];

const APPEARANCE_OPTIONS: {
  value: v4.OutlookCategoryAppearance;
  label: string;
}[] = [
  { value: 'suggested', label: 'Suggested · ✨' },
  { value: 'confirmed', label: 'Confirmed · ✅' },
  { value: 'plain', label: 'Plain category' },
];

const NAME_FORMAT_OPTIONS: {
  value: v4.OutlookCategoryNameFormat;
  label: string;
  description: string;
}[] = [
  {
    value: 'humanize',
    label: 'Readable name',
    description: 'support_request becomes Support Request',
  },
  {
    value: 'exact',
    label: 'Use the value exactly',
    description: 'Keep underscores, casing and punctuation as provided',
  },
];

export function OutlookCategoryForm({
  step,
  availableFields,
  stepOptions,
  readOnly,
  onPatch,
}: {
  step: v4.OutlookCategoryStepSpec;
  availableFields: FieldInfo[];
  stepOptions: StepOption[];
  readOnly: boolean;
  onPatch: (patch: Partial<v4.StepSpec>) => void;
}) {
  const operation = OPERATION_OPTIONS.find(
    (option) => option.value === step.operation,
  );
  const OperationIcon = operation?.icon ?? Tag;
  const appearance = step.appearance ?? 'plain';
  const nameFormat = step.nameFormat ?? 'humanize';
  const suffix = categorySuffix(appearance);
  const preview = step.category?.trim()
    ? `${step.category.trim()}${suffix}`
    : `Category value${suffix}`;

  return (
    <>
      <StepSection
        title="Update Outlook"
        description="Change the category on the email that started this workflow."
      >
        {readOnly ? (
          <ReadOnlyTextField
            label="Action"
            value={operation?.label ?? step.operation}
          />
        ) : (
          <TextField
            select
            label="Action"
            value={step.operation}
            onChange={(event) =>
              onPatch({
                operation: event.target.value as v4.OutlookCategoryOperation,
              })
            }
          >
            {OPERATION_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <MenuItem key={option.value} value={option.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon size={15} />
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
              );
            })}
          </TextField>
        )}

        <TemplateField
          label="Working message"
          helpText="Shown while Outlook is being updated."
          value={step.activity}
          fields={availableFields}
          onChange={(activity) => onPatch({ activity })}
          multiline={false}
          readOnly={readOnly}
        />

        <TemplateField
          label={
            step.operation === 'remove'
              ? 'Category to remove'
              : 'Category to set'
          }
          helpText="Enter a fixed name or insert a value from the workflow data."
          value={step.category}
          fields={availableFields}
          onChange={(category) => onPatch({ category })}
          multiline={false}
          readOnly={readOnly}
        />

        {readOnly ? (
          <ReadOnlyTextField
            label="Category state"
            value={
              APPEARANCE_OPTIONS.find((option) => option.value === appearance)
                ?.label
            }
          />
        ) : (
          <TextField
            select
            label="Category state"
            value={appearance}
            onChange={(event) =>
              onPatch({
                appearance: event.target.value as v4.OutlookCategoryAppearance,
              })
            }
            helperText="Markers make suggested and confirmed categories easy to recognise."
          >
            {APPEARANCE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {readOnly ? (
          <ReadOnlyTextField
            label="Name format"
            value={
              NAME_FORMAT_OPTIONS.find((option) => option.value === nameFormat)
                ?.label
            }
          />
        ) : (
          <TextField
            select
            label="Name format"
            value={nameFormat}
            onChange={(event) =>
              onPatch({
                nameFormat: event.target.value as v4.OutlookCategoryNameFormat,
              })
            }
            helperText={
              NAME_FORMAT_OPTIONS.find((option) => option.value === nameFormat)
                ?.description
            }
          >
            {NAME_FORMAT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.25,
            px: 1.25,
            py: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {step.operation === 'remove'
              ? 'This step removes'
              : 'This step sets'}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            <Chip
              size="small"
              icon={categoryOperationIcon(step.operation, OperationIcon)}
              label={preview}
              color={appearance === 'confirmed' ? 'success' : 'default'}
              variant="outlined"
            />
            {step.operation !== 'remove' && appearance === 'confirmed' && (
              <Check size={15} color="var(--mui-palette-success-main)" />
            )}
          </Stack>
        </Box>
      </StepSection>

      <Transitions description="When the category has been updated, continue to:">
        <TransitionTargetSelect
          stepOptions={stepOptions}
          label="Then continue to"
          value={step.next}
          onChange={(next) => onPatch({ next })}
          readOnly={readOnly}
        />
      </Transitions>
    </>
  );
}

function categoryOperationIcon(
  operation: v4.OutlookCategoryOperation,
  replaceIcon: typeof RefreshCw,
) {
  if (operation === 'remove') {
    return <Trash2 size={13} />;
  }
  if (operation === 'add') {
    return <Plus size={13} />;
  }
  const ReplaceIcon = replaceIcon;
  return <ReplaceIcon size={13} />;
}

function categorySuffix(appearance: v4.OutlookCategoryAppearance) {
  if (appearance === 'suggested') {
    return ' ✨';
  }
  if (appearance === 'confirmed') {
    return ' ✅';
  }
  return '';
}
